// DEPLOY: /var/www/woody.wtf/frontend/src/chart/gestures.js
// woody.wtf chart engine: one gesture layer for mouse, pen and touch (Pointer Events).
//   Mouse: drag = pan, wheel = zoom at cursor (horizontal wheel / trackpad = pan),
//          drag or wheel on the price axis = stretch prices, double-click = reset.
//   Touch: drag = pan, pinch = zoom between the fingers, tap = crosshair,
//          long-press then drag = move crosshair, double-tap = reset,
//          drag on the price axis = stretch prices.
//   Drawings: when a drawing tool is active, or a press lands on a drawing, the
//   gesture goes to the drawing editor (eng.editor) instead of panning.
//   Indicator panes: drag a pane's top edge to resize it.
//   Magnifier (touch): shown while drawing / dragging a drawing, while tapping point B of a
//   two-tap drawing, and in long-press crosshair mode; hidden when the finger lifts.
const MOVE_SLOP = 8;      // px before a press counts as a drag
const LONG_PRESS = 450;   // ms
const DOUBLE_TAP = 300;   // ms

export function attachGestures(el, eng) {
  const pts = new Map();  // pointerId -> {x, y}
  let mode = null;        // 'press' | 'pan' | 'axis' | 'pinch' | 'cross'
  let start = null, last = null, pinch = null, axisFrom = null, pressTimer = 0, lastTap = null, divider = -1, touch = false;

  const pos = e => { const r = el.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const onAxis = p => p.x > eng.plotR && p.y < eng.priceBottom;
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const stopPress = () => { clearTimeout(pressTimer); pressTimer = 0; };

  function down(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    el.setPointerCapture(e.pointerId);
    const p = pos(e);
    pts.set(e.pointerId, p);
    stopPress();
    if (pts.size === 2) {
      eng.setLoupe(null);
      if (mode === 'edit') eng.editor.cancelGesture();
      // Second finger: pinch, anchored on the candle under the starting midpoint.
      const [a, b] = [...pts.values()], m = mid(a, b);
      pinch = { d0: dist(a, b), w0: eng.barW, idx: eng.idxAt(m.x), m };
      mode = 'pinch';
      eng.setCross(null);
      return;
    }
    if (pts.size > 2) return;
    start = last = p;
    touch = e.pointerType !== 'mouse';
    divider = eng.dividerAt(p.y, e.pointerType !== 'mouse');
    if (divider >= 0) { mode = 'divider'; return; }
    if (onAxis(p)) { mode = 'axis'; axisFrom = eng.yRange(); return; }
    if (eng.editor?.down(p, e.pointerType, e.ctrlKey || e.metaKey || e.shiftKey)) {
      mode = 'edit'; eng.setCross(null);
      if (touch && ['create', 'handle'].includes(eng.editor.act?.kind)) eng.setLoupe(p);
      return;
    }
    mode = 'press';
    if (touch && eng.editor?.pending) eng.setLoupe(p);   // placing point B by tap
    if (touch) pressTimer = setTimeout(() => { mode = 'cross'; eng.setCross(start); eng.setLoupe(start); }, LONG_PRESS);
  }

  function move(e) {
    const p = pos(e);
    if (!pts.has(e.pointerId)) {
      // Plain mouse hover shows the crosshair.
      if (e.pointerType === 'mouse' && !e.buttons) {
        el.style.cursor = eng.dividerAt(p.y, false) >= 0 ? 'ns-resize' : '';
        eng.editor?.hover(p); eng.setCross(onAxis(p) ? null : p);
      }
      return;
    }
    pts.set(e.pointerId, p);
    if (mode === 'pinch' && pts.size >= 2) {
      const [a, b] = [...pts.values()], m = mid(a, b), d = dist(a, b);
      eng.barW = pinch.w0 * d / (pinch.d0 || 1);
      eng.clampView();
      // Keep the anchored candle under the fingers' current midpoint (zoom + pan together).
      eng.right = pinch.idx + (eng.plotR - m.x) / eng.barW;
      eng.clampView();
      eng.draw();
      return;
    }
    if (mode === 'edit') { eng.editor.move(p); if (touch && eng.editor.act?.moved) eng.setLoupe(p); return; }
    if (mode === 'divider') { eng.resizePane(divider, p.y - last.y); last = p; return; }
    if (mode === 'press' && dist(p, start) > MOVE_SLOP) { stopPress(); mode = 'pan'; eng.setCross(null); eng.setLoupe(null); }
    if (mode === 'pan') eng.panBy(p.x - last.x, p.y - last.y);
    else if (mode === 'axis') eng.scaleY(Math.exp((p.y - start.y) * 0.005), axisFrom);
    else if (mode === 'cross') { eng.setLoupe(p); eng.setCross(p); }
    last = p;
  }

  function up(e) {
    if (!pts.has(e.pointerId)) return;
    const p = pos(e);
    const wasTap = mode === 'press' && pts.size === 1;
    pts.delete(e.pointerId);
    stopPress();
    if (mode === 'pinch') {
      // Lifting one finger of a pinch hands over to a pan with the other one.
      if (pts.size === 1) { mode = 'pan'; last = [...pts.values()][0]; } else mode = null;
      return;
    }
    if (pts.size) return;
    eng.setLoupe(null);
    if (mode === 'edit') { eng.editor.up(p); mode = null; return; }
    if (wasTap && eng.editor?.tapB(p)) { mode = null; return; }   // point B of a two-tap drawing
    if (wasTap && e.pointerType !== 'mouse') {
      const now = Date.now();
      if (lastTap && now - lastTap.at < DOUBLE_TAP && dist(p, lastTap.p) < 30) {
        lastTap = null; eng.setCross(null); eng.reset();
      } else {
        lastTap = { at: now, p };
        eng.setCross(onAxis(p) ? null : p);
      }
    }
    mode = null;
  }

  function cancel(e) { pts.delete(e.pointerId); stopPress(); if (!pts.size) { mode = null; eng.setLoupe(null); } }
  function leave(e) { if (e.pointerType === 'mouse' && !pts.size) eng.setCross(null); }

  function wheel(e) {
    e.preventDefault();
    const p = pos(e);
    if (onAxis(p)) return eng.scaleY(Math.exp(e.deltaY * 0.002));
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return eng.panBy(-e.deltaX);
    eng.zoomAt(p.x, Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002)));  // ctrlKey = trackpad pinch
    eng.setCross(p);
  }

  function dbl(e) {
    e.preventDefault();
    if (eng.editor?.tool || eng.editor?.hitTest(pos(e), 6)) return;   // double-click on a drawing isn't a reset
    eng.reset();
  }

  el.addEventListener('pointerdown', down);
  el.addEventListener('pointermove', move);
  el.addEventListener('pointerup', up);
  el.addEventListener('pointercancel', cancel);
  el.addEventListener('pointerleave', leave);
  el.addEventListener('wheel', wheel, { passive: false });
  el.addEventListener('dblclick', dbl);
  return () => {
    stopPress();
    el.removeEventListener('pointerdown', down);
    el.removeEventListener('pointermove', move);
    el.removeEventListener('pointerup', up);
    el.removeEventListener('pointercancel', cancel);
    el.removeEventListener('pointerleave', leave);
    el.removeEventListener('wheel', wheel);
    el.removeEventListener('dblclick', dbl);
  };
}
